import { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Box, ListItem, Paper, Typography, Chip, FormControl, InputLabel, Select, MenuItem, OutlinedInput, InputAdornment, IconButton, Popover, Menu } from "@mui/material";
import { DoneAll, ArrowDropDown, QuestionMark } from "@mui/icons-material";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import { ResetButton, AddButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import Snackbar from "@plannel/components/Snackbar";
import {isEqual, cloneDeep, keys, isEmpty} from "lodash";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { FS } from "@plannel/utils/font-util";
import codeUtil from "@plannel/pages/utils/code-util";

const ComputeMeasureModal = (props) => {
  const { t, close, open, modalResultData, target, source, computeList } = props;
  const [displayFilterList, setDisplayFilterList] = useState([]);
  const filterItemText = useRef("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuEl, setMenuEl] = useState(null);
  const [expression, setExpression] = useState("");
  const [isIncorrect, setIsIncorrect] = useState(false);
  const [sourceMeasure, setSourceMeasure] = useState("");
  const [targetMeasure, setTargetMeasure] = useState("");
  const [targetList, setTargetList] = useState([]);
  const [computeObjList, setComputeObjList] = useState([]);

  const popoverOpen = Boolean(anchorEl);
  const id = popoverOpen ? 'simple-popover' : null;
  const menuOpen = Boolean(menuEl);

  const { snackBarMessageColor } = ColorUtil;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const measurePair = {
    "SF_QTY": "SF_AMT",
    "MF_QTY": "MF_AMT",
    "SF_AMT": "SF_QTY",
    "MF_AMT": "MF_QTY"
  }

  const popoverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const popoverClose = () => {
    setAnchorEl(null);
  };

  const menuKeyPress = (event) => {
    if (isEqual(event.key, "[")) {
      setMenuEl(event.currentTarget);
    }
  };

  const menuClose = () => {
    setMenuEl(null);
  };

  const refreshFilterList = () => {
    filterItemText.current = "";
    setDisplayFilterList([]);
    setComputeObjList([]);
    setSourceMeasure("");
    setTargetMeasure("");
    setExpression("");
  }

  const filterdTargetMeasure = (tarList, newData) => {
    const targetList = [];
    for (const measure of tarList) {
      if (measure.measureCd === Object.keys(newData)[0]) {
        const pairCd = target.filter(item => item.measureCd === measurePair[measure.measureCd]);
        if (pairCd.length > 0) {
          targetList.push(...tarList.filter(item => (item.measureCd !== measure.measureCd) && (item.measureCd !== pairCd[0].measureCd)));
        } else {
          targetList.push(...tarList.filter(item => item.measureCd !== measure.measureCd))
        }
      }
    }
    return targetList;
  };

  const filteredTargetMeasureByType = (measureNm, allComputeList) => {
    const sourceMeasureObj = source?.filter(sor => isEqual(GridUtils.gridValueL10N(sor.name), measureNm))[0];
    const sourceType = sourceMeasureObj?.valueTypeCd;
    let filterTarList = cloneDeep(target);
    for (const compute of allComputeList) {
      filterTarList = filterdTargetMeasure(filterTarList, compute);
    }
    // Suggested Manager Forecast Qty(SUGGESTED_MP_QTY)는 Manager Forecast(MF_QTY)를 채우는 용도로만 쓰인다 —
    // target measure를 Manager 하나로 고정한다(다른 QTY measure를 잘못 고르는 걸 막는다).
    if (sourceMeasureObj?.measureCd === codeUtil.dpMeasure.SUGGESTED_MP_QTY.cd) {
      setTargetList(filterTarList.filter(tar => tar.measureCd === codeUtil.dpMeasure.MF_QTY.cd));
      return;
    }
    setTargetList(filterTarList.filter(tar => isEqual(tar.valueTypeCd, sourceType)));
  }

  const changeCdExpression = (targetMeasure, targetExp) => {
    let cdExpression = targetExp;
    let cdTarget = targetMeasure;
    if (source.length > 0) {
      for (const measure of source) {
        if (cdExpression.includes(`[${GridUtils.gridValueL10N(measure.name)}]`)) {
          cdExpression = cdExpression.replaceAll(`[${GridUtils.gridValueL10N(measure.name)}]`, `[${measure.measureCd}]`);
        }
      }
      for (const measure of target) {
        if (cdTarget === GridUtils.gridValueL10N(measure.name)) {
          cdTarget = measure.measureCd;
        }
      }
    }
    return { [cdTarget]: cdExpression };
  }

  const changeNameExpression = (targetMeasure, targetExp) => {
    let cdExpression = targetExp;
    let cdTarget = targetMeasure;
    if (target.length > 0) {
      for (const measure of source) {
        if (cdExpression.includes(`[${measure.measureCd}]`)) {
          cdExpression = cdExpression.replaceAll(`[${measure.measureCd}]`, `[${GridUtils.gridValueL10N(measure.name)}]`);
        }
      }
      for (const measure of target) {
        if (cdTarget === measure.measureCd) {
          cdTarget = GridUtils.gridValueL10N(measure.name)
        }
      }
    }
    return `${cdTarget}: ${cdExpression}`;
  }

  const addExpression = (e) => {
    if (expression.match(/\]/) === null) {
      menuClose();
      setSourceMeasure(GridUtils.gridValueL10N(e.target.innerText));
      setExpression(`[${GridUtils.gridValueL10N(e.target.innerText)}]`);
    } else {
      menuClose();
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_SelectOneMeasure") });
    }
  }
  
  const addComputeList = () => {
    const displayData = `${targetMeasure}: ${expression}`;
    if (displayFilterList) {
      for (const filter of displayFilterList) {
        if (filter.split(":")[0] === targetMeasure) {
          setDisplayFilterList(displayFilterList.filter(chip => chip !== filter));
        }
      }
    }
    if (!expression) { 
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoSelectedSourceData") });
    } else if (!targetMeasure) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoSelectedUpdateTarget") });
    } else {
      const newData = changeCdExpression(targetMeasure, expression);
      const allComputeList = [...computeObjList, newData];
      setComputeObjList(allComputeList);
      setDisplayFilterList([...displayFilterList, displayData]);
      filteredTargetMeasureByType(sourceMeasure, allComputeList);
    }
    setTargetMeasure("");
  }

  const handleClose = () => {
    refreshFilterList();
    close({});
  };

  /*
   * 확정 시점의 source 는 화면(예: BF Source Data 꺼짐)에 따라 이미 좁혀져 있을 수 있다 — 예전에
   * source에 있던 측정치(POS_QTY 등)를 참조하는 수식이 남아 있으면, 그 참조는 지금 목록에서 안 보여서
   * 사용자가 고칠 방법이 없는데도 조용히 그대로 저장된다. 그런 수식이 하나라도 있으면 확정을 막고
   * 다시 확인하라고 안내한다.
   */
  const hasSourceOutCheck = () => {
    const sourceCds = new Set((source ?? []).map(measure => measure.measureCd));
    return computeObjList.some(compute =>
      Object.values(compute).some(expr => {
        const matched = expr?.match(/\[([^\]]+)]/);
        return matched && !sourceCds.has(matched[1]);
      })
    );
  };

  const handleConfirm = () => {
    if (hasSourceOutCheck()) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ReviewComputeFormula") });
      return;
    }
    modalResultData(computeObjList);
    close(modalResultData);
  };

  const handleComputeText = () => {
    const displayList = [];
    let filterTarget = target;
    const displayComputeList = computeList;
    for (const compute of displayComputeList) {
      for (const key of keys(compute)) {
        const value = compute[key];
        filterTarget = filterTarget.filter(item => item.measureCd !== key);
        filterTarget = filterTarget.filter(item => item.measureCd !== measurePair[key]);
        displayList.push(changeNameExpression(key, value));
      }
    }
    setComputeObjList([...displayComputeList]);
    setDisplayFilterList(displayList);
    setTargetList(filterTarget);
  };

  const handleDelete = (chipToDelete) => () => {
    setDisplayFilterList((chips) => chips.filter((chip) => chip !== chipToDelete));
    const chipExpression = chipToDelete.split(":")[1];
    const cdExpression = changeCdExpression(chipToDelete.split(":")[0], chipExpression)
    let index = 0;
    for (let i = 0; i < computeObjList.length; i++) {
      const keys = Object.keys(computeObjList[i])[0];
      if (keys === Object.keys(cdExpression)[0]) {
        index = i;
      }
    }
    computeObjList.splice(index, 1)

    // 이 칩을 만들 때 실제로 쓴 source measure 이름을 식(chipExpression)의 [ ] 안에서 그대로 꺼낸다 —
    // 지금 Source 드롭다운에 남아있는 값(sourceMeasure state)은 이 칩과 무관할 수 있어 타입 판정에 쓰면 안 된다.
    const chipSourceName = chipExpression?.match(/\[([^\]]+)\]/)?.[1];
    const sourceType = source?.filter(sor => isEqual(GridUtils.gridValueL10N(sor.name), chipSourceName))?.[0]?.valueTypeCd;

    for (const measure of target) {
      if (measure?.measureCd === Object.keys(cdExpression)[0]) {
        // when source measure's valueTypeCd is same as target measure's valueTypeCd
        if (sourceType === measure?.valueTypeCd) {
          setTargetList([...targetList, measure]);
        } else {
         const pairCd = target.filter(item => item?.measureCd === measurePair[measure?.measureCd]);
          setTargetList([...targetList, ...pairCd]);
        }
        break;
      }
    }
  };

  const handleChangeSelect = (event) => {
    const measureValue = event.target.value;
    if (isEqual(event.target.name, "sourceMeasure")) {
      setSourceMeasure(measureValue);
      setExpression(`[${measureValue}]`);
      filteredTargetMeasureByType(measureValue, computeObjList);
    } else if (isEqual(event.target.name, "targetMeasure")) {
      setTargetMeasure(measureValue);
    } else {
      setExpression(measureValue);
    }
  };

  useEffect(() => {
    if (open && source.length > 0) {
      handleComputeText();
    } else if (!open) {
      setSnackInfo({ open: false, severity: "", content: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (expression.match(/\]\[|\]+[1-9]|[1-9]+\[|\[\[/) !== null) {
      setIsIncorrect(true);
    } else {
      setIsIncorrect(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression]);

  return (
    /* handleConfirm 은 modalResultData 로 부모에 값을 넘기고 닫을 뿐 저장하지 않으므로 confirm 유지. */
    <BaseConfigModal
      open={open}
      onClose={handleClose}
      title={t("computeExpressSetting")}
      onSave={handleConfirm}
      saveLabel={t("confirm")}
      saveIcon={<DoneAll />}
      width={432}
    >
        <Box sx={{ textAlign: "center", gap: 1, border: 1, borderColor: 'grey.500', borderWidth: 0.5 }} >
          <Paper
            sx={{
              listStyle: 'none', maxHeight: 300, minHeight: 100, overflow: 'auto',
              p: 0.5, m: 0, mb: 1.5, mt: 1.5
            }}
            elevation={0}
            component="ul"
          >
            {displayFilterList.map((data) => {
              return data.length < 1 ? null : (
                <ListItem key={data}>
                  <Chip
                    sx={{ size: "small" }}
                    label={data}
                    onDelete={handleDelete(data)}
                  />
                </ListItem>
              );
            })}
          </Paper>
        </Box>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", display: "flex" }}>
            <FormControl size="small" sx={{width: "92%", mt: 2 }} required >
              <InputLabel>{t("sourceMeasure")}</InputLabel>
              <Select
                size="small"
                variant="outlined"
                name="sourceMeasure"
                label="Enabled Measure"
                value={sourceMeasure}
                defaultValue={""}
                onChange={(event) => { handleChangeSelect(event) }}
              >
                {source?.map((measure) => {
                  return <MenuItem value={GridUtils.gridValueL10N(measure.name)} key={`source_${(measure.measureCd)}`}>{GridUtils.gridValueL10N(measure.name)}</MenuItem>
                })}
              </Select>
            </FormControl>
          </Box>
          <Box size="small" style={{ width: "100%" }} sx={{ display: "flex", justifyContent: "center", mb: 0.5, mt: 0.5 }} >
            <ArrowDropDown color={"info"} />
          </Box>
          <Box sx={{ width: "100%", display: "flex" }}>
            <FormControl size="small" sx={{ textAlign: "left", width: "100%" }}>
              <InputLabel htmlFor="outlined-basic">{t("computeExpress")}</InputLabel>
              <OutlinedInput
                label={t("computeExpress")} id="outlined-basic" size="small" style={{}}
                value={expression}
                error={isIncorrect}
                multiline
                required
                onChange={handleChangeSelect}
                onKeyPress={menuKeyPress}
                placeholder="ex) [measure]*1.1"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="toggle password visibility"
                      onClick={popoverClick}
                      edge="end"
                    >
                      {<QuestionMark color="primary" sx={{ fontSize: "16px" }} />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
            <ResetButton sx={{ textAlign: "right" }} style={{ top: 4, left: 5 }} onClick={refreshFilterList} />
          </Box>
          <Box sx={{ width: "100%", display: "flex", mt: 1 }}>
            <FormControl size="small" sx={{ width: "100%" }} required >
              <InputLabel>{t("targetMeasure")}</InputLabel>
              <Select
                size="small"
                variant="outlined"
                name="targetMeasure"
                label="Enabled Measure"
                value={targetMeasure}
                defaultValue={""}
                onChange={(event) => { handleChangeSelect(event) }}
              >
                {targetList?.map((measure) => {
                  return <MenuItem value={GridUtils.gridValueL10N(measure.name)} key={`target_${(measure.measureCd)}`}>{GridUtils.gridValueL10N(measure.name)}</MenuItem>
                })}
              </Select>
            </FormControl>
            <AddButton sx={{ textAlign: "right" }} style={{ top: 4, left: 5 }} disabled={isEmpty(targetMeasure)} onClick={addComputeList} />
          </Box>
        </Box>
        <Snackbar
          open={snackInfo.open}
          onClose={() => setSnackInfo({ ...snackInfo, open: false })}
          severity={snackInfo.severity}
          content={snackInfo.content}
        />
        <Popover
          id={id}
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={popoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Typography sx={{ p: 1.2, fontSize: FS.H2 }}>
            {t("availableCals")}:<strong> + - * / </strong><br></br>
            {t("MSG_FormulaExplanation")} <br></br>
            ex&#41; &#91;MEASURE&#93;* 1.1</Typography>
        </Popover>
        <Menu
          id="basic-menu"
          MenuListProps={{
            'aria-labelledby': 'outlined-basic',
          }}
          anchorEl={menuEl}
          open={menuOpen}
          onClose={menuClose}
        >
          {source?.map((measure) => 
           <MenuItem onClick={addExpression} value={GridUtils.gridValueL10N(measure.name)} key={`source_pop_${(measure.measureCd)}`}>{GridUtils.gridValueL10N(measure.name)}</MenuItem>
          )}
        </Menu>
    </BaseConfigModal>
  )
}

export default withTranslation()(ComputeMeasureModal);